/**
 * Image rasterization port — decode, orient, resize, re-encode (M5 / S05-02).
 *
 * WHY THIS IS A PORT rather than inline code in the pipeline: this is the only
 * part of photo processing that needs a real graphics stack. `createImageBitmap`
 * and a 2D canvas context do not exist in the jsdom test environment, so
 * without this seam the entire pipeline would be untestable and every rule in
 * it would rest on reasoning. The byte-level work stays pure and directly
 * tested; exactly one call crosses into the browser.
 *
 * TWO PINNED DEFAULTS, BOTH DELIBERATE:
 *
 *   `imageOrientation: 'none'` — whether `createImageBitmap` applies EXIF
 *   orientation is BROWSER-VERSION DEPENDENT (I-040). We ask it not to, and
 *   rotate explicitly from the orientation we captured ourselves.
 *
 *   **AND WE NO LONGER TRUST THAT THE REQUEST WAS HONOURED.** On Jerry's
 *   Infinix (2026-08-14) it was not: the WebView applied a PNG's Orientation 6
 *   during decode despite the flag, this module applied it again, and every
 *   derivative came out turned twice. The pin was an assumption, and the fix is
 *   to MEASURE instead — see the capability probe and the decision rule in
 *   `render` below. The pin stays, because it is right wherever it works.
 *
 *   `colorSpace: 'srgb'` — Risk Probe C proved that stripping the canvas
 *   encoder's ICC profile is pixel-identical ONLY while the canvas is sRGB
 *   (I-039). Pinning it makes that proof permanent instead of incidental.
 *
 * No network. No secrets. Nothing here persists anything.
 */

export type EncodeMime = 'image/jpeg' | 'image/png';

export interface RenderRequest {
  /** Source bytes — the ORIGINAL, before sanitization (I-040 ordering). */
  readonly bytes: Uint8Array;
  readonly sourceMime: EncodeMime;
  /** EXIF orientation 1–8 captured from the original, or null when absent. */
  readonly orientation: number | null;
  /** Longest edge of the output, in pixels. Never upscales. */
  readonly longEdge: number;
  readonly encodeMime: EncodeMime;
  readonly quality: number;
  /**
   * The file's OWN stored dimensions, read from its header by our byte
   * inspector (SOFn / IHDR) — decoder-independent truth.
   *
   * Supplied so `render` can tell, FOR THIS IMAGE, whether the decoder already
   * applied the orientation: a decoded bitmap whose axes are swapped relative
   * to the header has been rotated by the decoder, and nothing else could have
   * done it. Optional because a caller that does not know them still gets the
   * capability probe's answer.
   */
  readonly storedWidth?: number | undefined;
  readonly storedHeight?: number | undefined;
}

/**
 * What the engine's decoder does with an EXIF/eXIf orientation tag when we
 * have explicitly asked it NOT to apply one.
 *
 *   'raw'     — obeys us and returns the stored pixels. We apply the transform.
 *   'applies' — applies the orientation anyway. We must apply NOTHING.
 *   'unknown' — could not be established. See `render` for the fallback.
 */
export type DecoderOrientationBehavior = 'raw' | 'applies' | 'unknown';

export interface RenderResult {
  readonly bytes: Uint8Array;
  /** Dimensions of what was actually produced, after any rotation. */
  readonly width: number;
  readonly height: number;
}

export interface ImageRasterPort {
  /** Decoded dimensions of the original, with orientation NOT applied. */
  readonly probe: (
    bytes: Uint8Array,
    mime: EncodeMime,
  ) => Promise<{ width: number; height: number }>;
  readonly render: (request: RenderRequest) => Promise<RenderResult>;
}

/**
 * EXIF orientation 5–8 exchange the axes: the stored pixels are rotated a
 * quarter turn relative to how the picture should be seen, so an upright
 * rendering of a 4000×3000 sensor image is 3000×4000.
 */
export function orientationSwapsAxes(orientation: number | null): boolean {
  return orientation === 5 || orientation === 6 || orientation === 7 || orientation === 8;
}

/**
 * The canvas transform for each EXIF orientation, against a canvas already
 * sized to the UPRIGHT dimensions.
 *
 * Written as an explicit table rather than derived, because the two mirrored
 * cases (2, 4, 5, 7) are exactly where a clever derivation goes wrong quietly —
 * a mirrored photo looks plausible until someone reads text in it.
 */
export function orientationTransform(
  orientation: number | null,
  width: number,
  height: number,
): { a: number; b: number; c: number; d: number; e: number; f: number } {
  switch (orientation) {
    case 2: // mirror horizontal
      return { a: -1, b: 0, c: 0, d: 1, e: width, f: 0 };
    case 3: // rotate 180
      return { a: -1, b: 0, c: 0, d: -1, e: width, f: height };
    case 4: // mirror vertical
      return { a: 1, b: 0, c: 0, d: -1, e: 0, f: height };
    case 5: // mirror horizontal, rotate 270 CW
      return { a: 0, b: 1, c: 1, d: 0, e: 0, f: 0 };
    case 6: // rotate 90 CW
      return { a: 0, b: 1, c: -1, d: 0, e: width, f: 0 };
    case 7: // mirror horizontal, rotate 90 CW
      return { a: 0, b: -1, c: -1, d: 0, e: width, f: height };
    case 8: // rotate 270 CW
      return { a: 0, b: -1, c: 1, d: 0, e: 0, f: height };
    case 1:
    default:
      return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  }
}

/**
 * Target size for a long edge, preserving aspect ratio and NEVER upscaling.
 * A 400 px thumbnail of a 300 px photo is still 300 px.
 */
export function fitWithin(
  width: number,
  height: number,
  longEdge: number,
): { width: number; height: number } {
  const scale = Math.min(1, longEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/* ==========================================================================
 * The decoder-orientation capability probe (I-040, real-device reproduction)
 * ========================================================================== */

/**
 * WHY THIS EXISTS, IN ONE SENTENCE: we asked the decoder not to apply the
 * orientation, it did anyway, and we applied it a second time.
 *
 * Jerry's Infinix, 2026-08-14, with a controlled 1200×800 PNG tagged
 * Orientation 6 that reached the server intact (`image/png`, 1200×800,
 * `source_orientation = 6`): the full-resolution viewer — which stores the
 * picture as it came and turns it with CSS at display time — was **correct**.
 * The display and thumbnail derivatives, built through this canvas path, were
 * turned **twice**: the arrow pointed right instead of up, exactly 180° from
 * the stored pixels.
 *
 * The comparison is what makes this a measurement rather than a theory. Both
 * paths use the same orientation table; the same file on the same phone came
 * out right through one and wrong through the other; **the only step that
 * differs is the decode.** So the table is correct and `imageOrientation:
 * 'none'` was not honoured.
 *
 * **The lesson is not "this engine is buggy" — it is that the old code ASSUMED
 * an answer it never measured.** The module header said the behaviour is
 * "BROWSER-VERSION DEPENDENT" and then pinned the flag and trusted the pin.
 * This asks instead.
 *
 * THE PROBE IS LOCAL AND INERT: the fixtures are two-pixel images this module
 * generates at runtime through the platform's own encoder, so they are
 * genuinely decodable by the very engine being measured. **No network, nothing
 * persisted, nothing uploaded, and not one byte of Brandon's data.** The
 * answer is cached per MIME family, so a Capture of twelve photos probes at
 * most once per format.
 */

const decoderBehavior = new Map<EncodeMime, DecoderOrientationBehavior>();

/** A real, engine-encoded image of the requested size. */
async function encodeTiny(
  mime: EncodeMime,
  width: number,
  height: number,
  paint: (context: CanvasRenderingContext2D) => void,
): Promise<Uint8Array> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { colorSpace: 'srgb' });
  if (!context) throw new Error('2D canvas context unavailable');
  paint(context);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mime, 1);
  });
  if (!blob) throw new Error('probe encoding produced nothing');
  return new Uint8Array(await blob.arrayBuffer());
}

/** Big-endian helpers — TIFF here is written big-endian ('MM'). */
const be16 = (value: number): number[] => [(value >> 8) & 0xff, value & 0xff];
const be32 = (value: number): number[] => [
  (value >>> 24) & 0xff,
  (value >>> 16) & 0xff,
  (value >>> 8) & 0xff,
  value & 0xff,
];

/** A minimal TIFF block whose single IFD entry is Orientation. */
function orientationTiff(orientation: number): number[] {
  return [
    0x4d,
    0x4d, // 'MM'
    ...be16(0x2a),
    ...be32(8),
    ...be16(1), // one entry
    ...be16(0x0112), // Orientation
    ...be16(3), // SHORT
    ...be32(1),
    ...be16(orientation),
    0,
    0,
    ...be32(0), // no next IFD
  ];
}

/** PNG chunk CRC — the one piece of PNG writing this module needs. */
function crc32(bytes: readonly number[]): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Insert an APP1 EXIF segment straight after SOI. */
function withJpegOrientation(bytes: Uint8Array, orientation: number): Uint8Array {
  const payload = [0x45, 0x78, 0x69, 0x66, 0, 0, ...orientationTiff(orientation)]; // 'Exif\0\0'
  const segment = [0xff, 0xe1, ...be16(payload.length + 2), ...payload];
  const out = new Uint8Array(bytes.length + segment.length);
  out.set(bytes.subarray(0, 2), 0); // SOI
  out.set(Uint8Array.from(segment), 2);
  out.set(bytes.subarray(2), 2 + segment.length);
  return out;
}

/** Insert an `eXIf` chunk before IEND. PNG carries raw TIFF, no 'Exif\0\0'. */
function withPngOrientation(bytes: Uint8Array, orientation: number): Uint8Array {
  const type = [0x65, 0x58, 0x49, 0x66]; // 'eXIf'
  const data = orientationTiff(orientation);
  const chunk = Uint8Array.from([
    ...be32(data.length),
    ...type,
    ...data,
    ...be32(crc32([...type, ...data])),
  ]);
  // IEND is the final 12 bytes of a well-formed PNG.
  const cut = bytes.length - 12;
  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.subarray(0, cut), 0);
  out.set(chunk, cut);
  out.set(bytes.subarray(cut), cut + chunk.length);
  return out;
}

const withOrientation = (bytes: Uint8Array, mime: EncodeMime, orientation: number): Uint8Array =>
  mime === 'image/png'
    ? withPngOrientation(bytes, orientation)
    : withJpegOrientation(bytes, orientation);

/**
 * Ask the decoder what it does, using the SAME call real derivatives use.
 *
 * **Step 1 — does it apply the tag at all?** A 2×1 image tagged Orientation 6
 * must come back 2×1 if we were obeyed and 1×2 if we were not. Dimensions
 * cannot be misread and need no pixel inspection.
 *
 * **Step 2 — if it applies, does it apply the MIRRORED half too?** Dimensions
 * cannot answer that, so this reads one pixel. A 2×1 image with a red left
 * pixel and a blue right pixel, tagged Orientation 2 (mirror horizontal),
 * must come back blue-on-the-left if the mirror was applied. **If the engine
 * turns out to rotate but not mirror, the answer is `unknown` rather than a
 * third, half-trusted mode** — inventing a partial transform from a machine
 * nobody has ever seen would be exactly the guessing this bug came from.
 */
export async function probeDecoderOrientation(
  mime: EncodeMime,
): Promise<DecoderOrientationBehavior> {
  const cached = decoderBehavior.get(mime);
  if (cached) return cached;

  let behavior: DecoderOrientationBehavior = 'unknown';
  try {
    const flat = await encodeTiny(mime, 2, 1, (context) => {
      context.fillStyle = '#ff0000';
      context.fillRect(0, 0, 1, 1);
      context.fillStyle = '#0000ff';
      context.fillRect(1, 0, 1, 1);
    });

    const rotated = await createImageBitmap(
      new Blob([withOrientation(flat, mime, 6) as BlobPart], { type: mime }),
      { imageOrientation: 'none' },
    );
    const swapped = rotated.width === 1 && rotated.height === 2;
    const untouched = rotated.width === 2 && rotated.height === 1;
    rotated.close();

    if (untouched) {
      behavior = 'raw';
    } else if (swapped) {
      // Step 2 — confirm the mirrored half is applied as well.
      const mirrored = await createImageBitmap(
        new Blob([withOrientation(flat, mime, 2) as BlobPart], { type: mime }),
        { imageOrientation: 'none' },
      );
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 2;
        canvas.height = 1;
        const context = canvas.getContext('2d', { colorSpace: 'srgb', willReadFrequently: true });
        if (context) {
          context.drawImage(mirrored, 0, 0);
          const left = context.getImageData(0, 0, 1, 1).data;
          // Blue on the left means the horizontal mirror was applied. JPEG is
          // lossy, so the channels are COMPARED rather than matched exactly.
          if (left[2]! > left[0]!) behavior = 'applies';
        }
      } finally {
        mirrored.close();
      }
    }
  } catch {
    // A probe that cannot run answers 'unknown'. It never throws into photo
    // processing: an internal capability question must not cost Brandon a
    // photo (PT §9).
    behavior = 'unknown';
  }

  decoderBehavior.set(mime, behavior);
  return behavior;
}

/** Test seam only — the cache is process-lifetime in the product. */
export function resetDecoderOrientationCache(): void {
  decoderBehavior.clear();
}

export function createWebImageRaster(): ImageRasterPort {
  const decode = async (bytes: Uint8Array, mime: EncodeMime): Promise<ImageBitmap> => {
    const blob = new Blob([bytes as BlobPart], { type: mime });
    // 'none' is the whole point — see the header.
    return await createImageBitmap(blob, { imageOrientation: 'none' });
  };

  return {
    probe: async (bytes, mime) => {
      const bitmap = await decode(bytes, mime);
      const size = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      return size;
    },

    render: async ({
      bytes,
      sourceMime,
      orientation,
      longEdge,
      encodeMime,
      quality,
      storedWidth,
      storedHeight,
    }) => {
      const bitmap = await decode(bytes, sourceMime);
      try {
        /**
         * DOES THIS BITMAP STILL NEED TURNING? — the I-040 decision, and the
         * whole point of the fix. Evidence is used in order of strength, and
         * the transform is applied EXACTLY ONCE or not at all.
         *
         * 1. THIS IMAGE'S OWN EVIDENCE, strongest and free: if the orientation
         *    swaps the axes and the decoded bitmap comes back swapped relative
         *    to the file's own header, the decoder rotated it. Nothing else
         *    could have. No probe, no inference — this photo, measured.
         * 2. THE CAPABILITY PROBE, for the orientations whose dimensions are
         *    unchanged (2, 3, 4) and where evidence 1 is therefore silent.
         * 3. FALLBACK when neither is decisive: behave exactly as this module
         *    always has and apply the transform. That keeps an ambiguous probe
         *    from silently changing behaviour for the overwhelmingly common
         *    orientation 1 — where every branch is identical anyway.
         */
        const swapsAxes = orientationSwapsAxes(orientation);
        const measuredApplied =
          swapsAxes &&
          storedWidth !== undefined &&
          storedHeight !== undefined &&
          bitmap.width === storedHeight &&
          bitmap.height === storedWidth;
        const decoderApplied =
          measuredApplied ||
          (!swapsAxes && (await probeDecoderOrientation(sourceMime)) === 'applies');

        // THE BITMAP IS ALREADY UPRIGHT when the decoder applied the tag, so
        // its own axes are the upright ones and no transform is owed.
        const swapped = swapsAxes && !decoderApplied;
        const uprightWidth = swapped ? bitmap.height : bitmap.width;
        const uprightHeight = swapped ? bitmap.width : bitmap.height;
        const target = fitWithin(uprightWidth, uprightHeight, longEdge);

        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;
        const context = canvas.getContext('2d', { colorSpace: 'srgb' });
        if (!context) throw new Error('2D canvas context unavailable');

        // EXACTLY ONCE, OR NOT AT ALL. `orientation: 1` is the identity, so a
        // decoder that already applied the tag is handed the identity here.
        const transform = orientationTransform(
          decoderApplied ? 1 : orientation,
          target.width,
          target.height,
        );
        context.setTransform(
          transform.a,
          transform.b,
          transform.c,
          transform.d,
          transform.e,
          transform.f,
        );
        // Drawn at the PRE-rotation extent, because the transform above maps
        // that rectangle onto the upright canvas.
        context.drawImage(
          bitmap,
          0,
          0,
          swapped ? target.height : target.width,
          swapped ? target.width : target.height,
        );

        const encoded = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, encodeMime, quality);
        });
        if (!encoded) throw new Error('canvas encoding produced nothing');

        return {
          bytes: new Uint8Array(await encoded.arrayBuffer()),
          width: target.width,
          height: target.height,
        };
      } finally {
        bitmap.close();
      }
    },
  };
}

/**
 * Selected once. Both runtimes use the same implementation: `createImageBitmap`
 * and canvas are standard capabilities inside the Chromium WebView Capacitor
 * uses on Android and inside WKWebView on iOS, and Risk Probe C exercised this
 * exact path on real Android hardware. A native image plugin would add a
 * dependency to replace something already proven to work.
 */
export function selectImageRaster(): ImageRasterPort {
  return createWebImageRaster();
}
